/**
 * CPD — Learn Library Page (auth-gated, /learn)
 *
 * Thumbnail grid with:
 *  - "All" | "Saved" top-level tab
 *  - Category filter chips (within All)
 *  - Search bar
 *  - Bookmark toggle on each card (optimistic, POST/DELETE /api/modules/:id/watchlist)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { api, ApiError } from '@/lib/api';
import { getYouTubeThumbnail } from '@/lib/utils';
import type { ModuleSummary } from '@/lib/types';

const HIGHLIGHT_CATEGORIES = new Set(['Required', 'New']);

function tagStyle(category: string): string {
  return HIGHLIGHT_CATEGORIES.has(category)
    ? 'bg-[var(--stitch-tertiary-fixed)] text-[var(--stitch-on-tertiary-fixed-variant)]'
    : 'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]';
}

interface ModuleCardProps {
  m: ModuleSummary;
  onToggleSave: (id: string, currentlySaved: boolean) => void;
}

function ModuleCard({ m, onToggleSave }: ModuleCardProps): React.ReactElement {
  const thumbnail = getYouTubeThumbnail(m.videoUrl);
  return (
    <div className="group flex flex-col gap-2">
      <div className="relative aspect-video rounded-xl overflow-hidden bg-[var(--stitch-surface-container-high)]">
        <Link href={`/modules/${m.id}`} className="block w-full h-full no-underline">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="material-symbols-outlined text-[var(--stitch-outline)] text-5xl">{m.icon}</span>
            </div>
          )}
        </Link>

        {/* Completed badge */}
        {m.completed && (
          <div className="absolute top-2 right-2 bg-green-600 rounded-full p-0.5 pointer-events-none" title="Completed">
            <span className="material-symbols-outlined text-white text-[18px] is-filled leading-none">check_circle</span>
          </div>
        )}

        {/* Bookmark toggle */}
        <button
          type="button"
          aria-label={m.saved ? 'Remove from watchlist' : 'Add to watchlist'}
          aria-pressed={m.saved}
          onClick={e => { e.preventDefault(); onToggleSave(m.id, m.saved); }}
          className="absolute top-2 left-2 bg-black/40 hover:bg-black/60 rounded-full p-1 transition-colors"
        >
          <span className={`material-symbols-outlined text-[18px] leading-none ${m.saved ? 'is-filled text-yellow-400' : 'text-white'}`}>
            bookmark
          </span>
        </button>
      </div>

      <Link href={`/modules/${m.id}`} className="no-underline flex flex-col gap-1 px-0.5">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full stitch-font-label text-[11px] ${tagStyle(m.category)}`}>
            {m.category}
          </span>
          <span className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)]">{m.hours} hr</span>
        </div>
        <h3 className="stitch-font-headline text-[14px] md:text-[15px] text-[var(--stitch-primary)] leading-snug line-clamp-2 uppercase">
          {m.title}
        </h3>
      </Link>
    </div>
  );
}

function LearnLibraryContent(): React.ReactElement {
  const [modules, setModules] = useState<ModuleSummary[] | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState<string>('All Topics');
  const [tab, setTab] = useState<'all' | 'saved'>('all');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [modulesResult, catsResult] = await Promise.all([
          api.get<{ modules: ModuleSummary[] }>('/api/modules'),
          api.get<{ categories: string[] }>('/api/modules/categories'),
        ]);
        if (!cancelled) {
          setModules(modulesResult.modules);
          setCategories(catsResult.categories);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load modules.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggleSave = useCallback((id: string, currentlySaved: boolean): void => {
    // Optimistic update
    setModules(prev => prev
      ? prev.map(m => m.id === id ? { ...m, saved: !currentlySaved } : m)
      : prev
    );
    const req = currentlySaved
      ? api.delete(`/api/modules/${id}/watchlist`)
      : api.post(`/api/modules/${id}/watchlist`, {});
    req.catch(() => {
      // Revert on failure
      setModules(prev => prev
        ? prev.map(m => m.id === id ? { ...m, saved: currentlySaved } : m)
        : prev
      );
    });
  }, []);

  const filtered = useMemo(() => {
    if (!modules) return [];
    const q = query.trim().toLowerCase();
    return modules.filter(m => {
      if (tab === 'saved' && !m.saved) return false;
      if (tab === 'all' && activeCat !== 'All Topics' && m.category !== activeCat) return false;
      if (q && !m.title.toLowerCase().includes(q) && !m.description.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [modules, query, activeCat, tab]);

  const savedCount = modules ? modules.filter(m => m.saved).length : 0;

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)]">
      <header className="mb-8 max-w-3xl">
        <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] mb-4 tracking-tight">
          Learn
        </h1>
        <p className="stitch-font-body text-[16px] text-[var(--stitch-on-surface-variant)] leading-relaxed">
          Curated modules across the chiropractic curriculum. Complete the quiz to earn your CPD certificate.
        </p>
      </header>

      {/* All / Saved tab strip */}
      <div className="flex items-center gap-1 mb-6 border-b border-[var(--stitch-outline-variant)]">
        {([['all', 'All modules'], ['saved', `Saved${savedCount > 0 ? ` (${savedCount})` : ''}`]] as const).map(([t, label]) => (
          <button
            key={t}
            type="button"
            onClick={() => { setTab(t); }}
            className={`px-4 py-2.5 stitch-font-label text-[13px] uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              tab === t
                ? 'text-[var(--stitch-primary)] border-[var(--stitch-primary)] font-semibold'
                : 'text-[var(--stitch-on-surface-variant)] border-transparent hover:text-[var(--stitch-primary)]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search + category filter (only in All tab) */}
      <div className="mb-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between bg-[var(--stitch-surface-container-low)] p-4 rounded-xl border border-[var(--stitch-outline-variant)]">
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[var(--stitch-on-surface-variant)]">search</span>
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); }}
            placeholder="Search content library…"
            className="w-full pl-10 pr-4 py-2 bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-lg focus:ring-1 focus:ring-[var(--stitch-primary)] focus:border-[var(--stitch-primary)] outline-none stitch-font-body text-sm"
            aria-label="Search content library"
          />
        </div>
        {tab === 'all' && (
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto" role="tablist" aria-label="Filter by category">
            {(['All Topics', ...categories] as string[]).map(cat => {
              const active = cat === activeCat;
              return (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => { setActiveCat(cat); }}
                  className={`px-3 py-1.5 rounded-full stitch-font-label text-[12px] whitespace-nowrap normal-case tracking-normal transition-colors ${
                    active
                      ? 'bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)]'
                      : 'bg-[var(--stitch-surface-container-highest)] text-[var(--stitch-on-surface-variant)] hover:bg-[var(--stitch-outline-variant)]'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-[var(--color-error-100,#fee2e2)] px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!modules && !error && (
        <div className="flex items-center justify-center py-24">
          <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
        </div>
      )}

      {modules && (
        <>
          {tab === 'saved' && savedCount === 0 ? (
            <div className="flex flex-col items-center gap-4 py-20 text-center">
              <span className="material-symbols-outlined text-5xl text-[var(--stitch-outline)]">bookmark</span>
              <p className="stitch-font-body text-[var(--stitch-on-surface-variant)] max-w-xs">
                No saved modules yet. Click the bookmark icon on any module to add it to your watchlist.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map(m => (
                <ModuleCard key={m.id} m={m} onToggleSave={handleToggleSave} />
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full text-center py-12 text-[var(--stitch-on-surface-variant)]">
                  No modules match your filter.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function LearnLibraryPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/learn">
      <LearnLibraryContent />
    </AppLayout>
  );
}
